// Lead source tracking utilities
export const LEAD_SOURCES = {
  WEBSITE: "website",
  SOCIAL_MEDIA: "social_media",
  REFERRAL: "referral",
  CAMPAIGN: "campaign",
  DIRECT: "direct",
  SEARCH: "search",
  EMAIL: "email",
};

export const SOCIAL_PLATFORMS = {
  FACEBOOK: "facebook",
  INSTAGRAM: "instagram",
  YOUTUBE: "youtube",
  LINKEDIN: "linkedin",
  TWITTER: "twitter",
  WHATSAPP: "whatsapp",
};

export const CAMPAIGN_TYPES = {
  PAID_AD: "paid_ad",
  ORGANIC: "organic",
  INFLUENCER: "influencer",
  PARTNERSHIP: "partnership",
};

/**
 * Captures lead source information from URL parameters and browser data
 * @returns {Object} Lead source tracking data
 */
export const captureLeadSource = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = document.referrer;
    const currentUrl = window.location.href;

    // Get UTM parameters
    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmContent = urlParams.get("utm_content");
    const utmTerm = urlParams.get("utm_term");

    // Get custom parameters
    const campaignId = urlParams.get("campaign_id") || urlParams.get("cid");
    const referralCode = urlParams.get("ref") || urlParams.get("referral");
    const sourceParam = urlParams.get("source");

    console.log("🎯 Lead Tracking Debug:", {
      utmSource,
      utmMedium,
      utmCampaign,
      campaignId,
      referralCode,
      sourceParam,
      currentUrl,
    });

    // Determine lead source based on available data
    let leadSource = LEAD_SOURCES.DIRECT;
    let subSource = null;
    let detectedCampaignId = campaignId; // Always track campaign ID if present

    // Priority order for source detection: UTM source has highest priority
    if (referralCode) {
      leadSource = LEAD_SOURCES.REFERRAL;
      subSource = referralCode;
    } else if (utmSource) {
      // Map UTM sources to our categories
      if (utmSource.includes("facebook") || utmSource.includes("fb")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.FACEBOOK;
      } else if (utmSource.includes("instagram") || utmSource.includes("ig")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.INSTAGRAM;
      } else if (utmSource.includes("youtube") || utmSource.includes("yt")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.YOUTUBE;
      } else if (utmSource.includes("linkedin")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.LINKEDIN;
      } else if (utmSource.includes("twitter")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.TWITTER;
      } else if (utmSource.includes("whatsapp")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.WHATSAPP;
      } else if (utmSource.includes("google") || utmSource.includes("bing")) {
        leadSource = LEAD_SOURCES.SEARCH;
        subSource = utmSource;
      } else if (utmSource.includes("email")) {
        leadSource = LEAD_SOURCES.EMAIL;
        subSource = utmMedium || "email";
      } else {
        leadSource = LEAD_SOURCES.CAMPAIGN;
        subSource = utmSource;
      }
    } else if (campaignId) {
      // Only use campaign as source if there's no UTM source
      leadSource = LEAD_SOURCES.CAMPAIGN;
    } else if (sourceParam) {
      leadSource = sourceParam;
    } else if (referrer) {
      // Detect source from referrer
      const referrerDomain = new URL(referrer).hostname.toLowerCase();

      if (referrerDomain.includes("facebook.com")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.FACEBOOK;
      } else if (referrerDomain.includes("instagram.com")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.INSTAGRAM;
      } else if (referrerDomain.includes("youtube.com")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.YOUTUBE;
      } else if (referrerDomain.includes("linkedin.com")) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.LINKEDIN;
      } else if (
        referrerDomain.includes("twitter.com") ||
        referrerDomain.includes("t.co")
      ) {
        leadSource = LEAD_SOURCES.SOCIAL_MEDIA;
        subSource = SOCIAL_PLATFORMS.TWITTER;
      } else if (referrerDomain.includes("google.com")) {
        leadSource = LEAD_SOURCES.SEARCH;
        subSource = "google";
      } else if (referrerDomain.includes("bing.com")) {
        leadSource = LEAD_SOURCES.SEARCH;
        subSource = "bing";
      } else {
        leadSource = LEAD_SOURCES.WEBSITE;
        subSource = referrerDomain;
      }
    }

    return {
      source: leadSource,
      subSource,
      campaignId: detectedCampaignId,
      utmData: {
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
        content: utmContent,
        term: utmTerm,
      },
      referrer,
      referralCode,
      currentUrl,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Lead tracking error:", error);
    return {
      source: LEAD_SOURCES.DIRECT,
      subSource: null,
      campaignId: null,
      utmData: {},
      referrer: "",
      referralCode: null,
      currentUrl: window.location.href,
      timestamp: new Date().toISOString(),
    };
  }
};

/**
 * Stores lead source data in localStorage for persistence across pages
 * @param {Object} sourceData Lead source data to store
 */
export const storeLeadSource = (sourceData) => {
  try {
    localStorage.setItem("leadSourceData", JSON.stringify(sourceData));
    sessionStorage.setItem("leadSourceData", JSON.stringify(sourceData));
  } catch (error) {
    console.warn("Failed to store lead source data:", error);
  }
};

/**
 * Retrieves stored lead source data
 * @returns {Object|null} Stored lead source data or null
 */
export const getStoredLeadSource = () => {
  try {
    // Try sessionStorage first (current session), then localStorage (persistent)
    const sessionData = sessionStorage.getItem("leadSourceData");
    const localData = localStorage.getItem("leadSourceData");

    const data = sessionData || localData;
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.warn("Failed to retrieve lead source data:", error);
    return null;
  }
};

/**
 * Clears stored lead source data (useful after successful registration)
 */
export const clearLeadSource = () => {
  try {
    localStorage.removeItem("leadSourceData");
    sessionStorage.removeItem("leadSourceData");
  } catch (error) {
    console.warn("Failed to clear lead source data:", error);
  }
};

/**
 * Formats source data for display in UI
 * @param {Object} sourceData Lead source data
 * @returns {string} Formatted source string
 */
export const formatLeadSource = (sourceData) => {
  if (!sourceData) return "Direct";

  const { source, subSource, utmData } = sourceData;

  if (source === LEAD_SOURCES.SOCIAL_MEDIA && subSource) {
    return `${
      subSource.charAt(0).toUpperCase() + subSource.slice(1)
    } (Social Media)`;
  }

  if (source === LEAD_SOURCES.CAMPAIGN && utmData?.campaign) {
    return `Campaign: ${utmData.campaign}`;
  }

  if (source === LEAD_SOURCES.REFERRAL && sourceData.referralCode) {
    return `Referral: ${sourceData.referralCode}`;
  }

  if (source === LEAD_SOURCES.SEARCH && subSource) {
    return `${subSource.charAt(0).toUpperCase() + subSource.slice(1)} Search`;
  }

  if (source === LEAD_SOURCES.WEBSITE && subSource) {
    return `Website: ${subSource}`;
  }

  return source.charAt(0).toUpperCase() + source.slice(1).replace("_", " ");
};

/**
 * Initializes lead source tracking when page loads
 * Should be called once when the app loads
 */
export const initializeLeadTracking = () => {
  // Only capture if we don't already have data or if there are new URL parameters
  const existingData = getStoredLeadSource();
  const urlParams = new URLSearchParams(window.location.search);
  const hasTrackingParams =
    urlParams.has("utm_source") ||
    urlParams.has("campaign_id") ||
    urlParams.has("ref") ||
    urlParams.has("source");

  // Capture new data if we have tracking parameters or no existing data
  if (hasTrackingParams || !existingData) {
    const sourceData = captureLeadSource();
    storeLeadSource(sourceData);
    return sourceData;
  }

  return existingData;
};
